import { Drawer } from '@components/drawer'
import { noop } from 'lodash'
import { drawerContentTypesMapping } from '@core/context/DrawerContentTypes'
import { DrawerNavigationHeader } from '@ui/drawer/common/DrawerNavigationHeader'
import {
    PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import { DrawerContextType, DrawerState } from './DrawerContext.types'

export type { DrawerSnapshot, DrawerState } from './DrawerContext.types'

const CLOSE_ANIMATION_MS = 500

const initialDrawerState: DrawerState = {
    isDrawerOpen: false,
    drawerContentType: null,
    onClose: noop,
    contentProps: {},
}

export const DrawerContext = createContext<DrawerContextType>({
    drawerState: initialDrawerState,
    setDrawerState: () => {},
})

export const DrawerProvider = ({ children }: PropsWithChildren<unknown>) => {
    const [drawerState, setDrawerState] =
        useState<DrawerState>(initialDrawerState)
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const { drawerContentType, isDrawerOpen, previousState, contentProps } =
        drawerState
    const screen = drawerContentType
        ? drawerContentTypesMapping[drawerContentType]
        : null
    const Body = screen?.Body

    const onClose = () => {
        drawerState.onClose?.()
        setDrawerState((prev) => ({ ...prev, isDrawerOpen: false }))
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
        closeTimerRef.current = setTimeout(() => {
            setDrawerState((prev) => ({
                ...prev,
                drawerContentType: null,
                contentProps: {},
                previousState: undefined,
            }))
        }, CLOSE_ANIMATION_MS)
    }

    const onBack = previousState
        ? () =>
              setDrawerState((prev) => ({
                  ...prev,
                  drawerContentType: previousState.drawerContentType,
                  contentProps: previousState.contentProps,
                  previousState: previousState.previousState,
              }))
        : undefined

    useEffect(() => {
        window.Intercom('update', { hide_default_launcher: isDrawerOpen })
    }, [isDrawerOpen])

    useEffect(() => {
        if (isDrawerOpen) return

        setDrawerState((prev) =>
            prev.onClose === noop ? prev : { ...prev, onClose: noop }
        )
    }, [isDrawerOpen])

    useEffect(
        () => () => {
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
        },
        []
    )

    const contextValue = useMemo(
        () => ({ drawerState, setDrawerState }),
        [drawerState]
    )

    return (
        <DrawerContext.Provider value={contextValue}>
            {children}
            <Drawer
                onClose={onClose}
                onBack={onBack}
                isOpen={isDrawerOpen}
                width={contentProps?.width}
                header={
                    screen?.header ? (
                        <DrawerNavigationHeader config={screen.header} />
                    ) : undefined
                }
            >
                {Body && <Body />}
            </Drawer>
        </DrawerContext.Provider>
    )
}

export const useDrawer = () => useContext(DrawerContext)
